import { useState, useEffect, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import { SeoSchema, BreadcrumbSchema, getArticleSchema, getWebPageSchema } from '@/components/base/SeoSchema';
import { comics, categories } from '@/mocks/comics';

export default function ComicDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'info' | 'chapters'>('info');

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setActiveTab('info');
  }, [id]);

  const comic = useMemo(() => comics.find(c => c.id === id), [id]);

  if (!comic) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-20 text-center">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center text-foreground-300">
              <i className="ri-error-warning-line text-5xl"></i>
            </div>
            <h1 className="text-xl font-bold text-foreground-950 mb-2">漫画未找到</h1>
            <p className="text-foreground-500 mb-6">抱歉，您要查看的漫画不存在或已被移除。</p>
            <Link
              to="/"
              className="inline-flex items-center px-5 py-2.5 bg-primary-500 text-background-50 rounded-md hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const categoryInfo = categories.find(c => c.slug === comic.category);

  return (
    <>
      <SeoSchema
        type="WebPage"
        data={getWebPageSchema(
          `${comic.title} - 免费韩漫在线阅读 | 韩漫阅读`,
          `${comic.title}：${comic.description.substring(0, 100)}。免费无删减下拉式在线阅读，作者${comic.author}，${comic.status}，评分${comic.rating}分。`,
          `https://hanmanyuedu.com/comic/${comic.id}`
        ) as Record<string, unknown>}
      />
      <SeoSchema type="Article" data={getArticleSchema(comic.title, comic.description, comic.author, comic.chapters[0]?.updateDate || comic.updateDate, comic.cover) as Record<string, unknown>} />
      <BreadcrumbSchema
        items={[
          { name: '首页', item: 'https://hanmanyuedu.com' },
          { name: categoryInfo?.name || '漫画', item: `https://hanmanyuedu.com/category/${comic.category}` },
          { name: comic.title, item: `https://hanmanyuedu.com/comic/${comic.id}` },
        ]}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="max-w-4xl mx-auto px-4 md:px-6 py-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: categoryInfo?.name || '漫画', href: `/category/${comic.category}` },
                { label: comic.title },
              ]}
            />

            <div className="flex flex-col md:flex-row gap-6 md:gap-8 mb-8">
              <div className="md:w-64 flex-shrink-0">
                <div className="aspect-[3/4] rounded-lg overflow-hidden bg-background-100 border border-background-200/70">
                  <img
                    src={comic.cover}
                    alt={`${comic.title} - 韩国漫画封面`}
                    title={`${comic.title} - 免费韩漫`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="flex-1 min-w-0">
                <h1 className="text-xl md:text-2xl font-bold text-foreground-950 mb-3" style={{ fontFamily: 'var(--font-heading)' }}>
                  {comic.title}
                </h1>

                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <span className={`px-2.5 py-1 text-xs font-medium rounded-full ${
                    comic.status === '连载中'
                      ? 'bg-accent-500 text-background-50'
                      : 'bg-foreground-500 text-background-50'
                  }`}>
                    {comic.status}
                  </span>
                  <span className="text-sm text-foreground-500">
                    <div className="w-3.5 h-3.5 inline-flex items-center justify-center mr-1">
                      <i className="ri-user-line text-xs"></i>
                    </div>
                    {comic.author}
                  </span>
                  <span className="text-sm text-foreground-500">
                    <div className="w-3.5 h-3.5 inline-flex items-center justify-center mr-1">
                      <i className="ri-star-fill text-yellow-500 text-xs"></i>
                    </div>
                    {comic.rating}
                  </span>
                  <span className="text-sm text-foreground-500">
                    <div className="w-3.5 h-3.5 inline-flex items-center justify-center mr-1">
                      <i className="ri-eye-line text-xs"></i>
                    </div>
                    {comic.views > 10000 ? `${(comic.views / 10000).toFixed(1)}万` : comic.views}
                  </span>
                </div>

                <div className="flex flex-wrap gap-1.5 mb-4">
                  {comic.tags.map((tag) => (
                    <span key={tag} className="px-2 py-0.5 text-xs bg-secondary-100 text-secondary-800 rounded-full">
                      {tag}
                    </span>
                  ))}
                </div>

                <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                  {comic.description}
                </p>

                <div className="text-xs text-foreground-500 mb-6">
                  最近更新：{comic.updateDate} · 共 {comic.chapters.length} 话
                </div>

                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    onClick={() => setActiveTab('chapters')}
                    className="inline-flex items-center justify-center px-6 py-2.5 bg-primary-500 text-background-50 font-medium rounded-md hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <div className="w-4 h-4 flex items-center justify-center mr-2">
                      <i className="ri-book-open-line"></i>
                    </div>
                    开始阅读
                  </button>
                  <button
                    onClick={() => {
                      if (comic.chapters.length > 0) {
                        navigate(`/comic/${comic.id}?tab=chapters`);
                        setActiveTab('chapters');
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }
                    }}
                    className="inline-flex items-center justify-center px-6 py-2.5 border border-background-300/60 text-foreground-700 font-medium rounded-md hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <div className="w-4 h-4 flex items-center justify-center mr-2">
                      <i className="ri-list-check"></i>
                    </div>
                    查看目录（{comic.chapters.length}话）
                  </button>
                </div>
              </div>
            </div>

            {activeTab === 'chapters' && (
              <section className="mb-8">
                <h2 className="text-lg font-bold text-foreground-950 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
                  <a href="#chapter-list">章节列表</a>
                </h2>
                <div className="border border-background-200/70 rounded-lg overflow-hidden divide-y divide-background-200/70">
                  {comic.chapters.map((chapter) => (
                    <div
                      key={chapter.id}
                      className="flex items-center justify-between px-4 py-3 hover:bg-background-50 transition-colors"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <span className="text-sm font-medium text-foreground-900 whitespace-nowrap">
                          {chapter.title}
                        </span>
                      </div>
                      <span className="text-xs text-foreground-500 whitespace-nowrap ml-4">
                        {chapter.updateDate}
                      </span>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {activeTab === 'chapters' && comic.chapters.length > 0 && (
              <section className="mb-8">
                <h2 className="text-lg font-bold text-foreground-950 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
                  <a href="#comic-reader">下拉式阅读</a>
                </h2>
                <div className="bg-background-100 border border-background-200/70 rounded-lg p-8 md:p-12">
                  <div className="max-w-lg mx-auto space-y-6">
                    <div className="bg-background-50 rounded-lg p-6 border border-background-200/70">
                      <div className="w-full aspect-[3/4] bg-background-200/50 rounded-md mb-4 flex items-center justify-center">
                        <div className="w-16 h-16 flex items-center justify-center text-foreground-300">
                          <i className="ri-image-line text-4xl"></i>
                        </div>
                      </div>
                      <p className="text-sm text-foreground-700 leading-relaxed">
                        这里是《{comic.title}》{comic.chapters[0].title}的漫画内容区域。在完整版本中，您可以通过向下滑动页面来连续阅读漫画的每一页内容，获得流畅的下拉式阅读体验。
                      </p>
                    </div>

                    <div className="bg-background-50 rounded-lg p-6 border border-background-200/70">
                      <div className="w-full aspect-[3/4] bg-background-200/50 rounded-md mb-4 flex items-center justify-center">
                        <div className="w-16 h-16 flex items-center justify-center text-foreground-300">
                          <i className="ri-image-line text-4xl"></i>
                        </div>
                      </div>
                      <p className="text-sm text-foreground-700 leading-relaxed">
                        下拉继续阅读...漫画的每一页都会按照顺序纵向排列，您只需向下滑动即可无缝阅读后续内容。
                      </p>
                    </div>

                    <div className="text-center py-8">
                      <p className="text-sm text-foreground-500 mb-4">
                        — 下拉式阅读体验，向下滑动继续 —
                      </p>
                      <div className="flex items-center justify-center gap-4">
                        <button className="px-4 py-2 bg-background-200/70 text-foreground-600 text-sm rounded-md cursor-pointer whitespace-nowrap hover:bg-background-300/70 transition-colors">
                          上一话
                        </button>
                        <span className="text-sm text-foreground-500">
                          {comic.chapters[0].title}
                        </span>
                        <button className="px-4 py-2 bg-primary-500 text-background-50 text-sm rounded-md cursor-pointer whitespace-nowrap hover:bg-primary-600 transition-colors">
                          下一话
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </section>
            )}

            <section className="mb-8">
              <h2 className="text-lg font-bold text-foreground-950 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
                <a href="#related-comics">相关推荐</a>
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                {comics
                  .filter(c => c.id !== comic.id && (c.category === comic.category || c.tags.some(t => comic.tags.includes(t))))
                  .slice(0, 4)
                  .map((related) => (
                    <Link
                      key={related.id}
                      to={`/comic/${related.id}`}
                      className="group block rounded-lg overflow-hidden bg-background-50 border border-background-200/70 hover:border-primary-300 transition-all duration-200"
                    >
                      <div className="aspect-[3/4] overflow-hidden bg-background-100">
                        <img
                          src={related.cover}
                          alt={related.title}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                        />
                      </div>
                      <div className="p-2.5">
                        <h3 className="text-sm font-medium text-foreground-950 group-hover:text-primary-500 transition-colors line-clamp-1">
                          {related.title}
                        </h3>
                        <p className="text-xs text-foreground-500 mt-0.5">{related.author}</p>
                      </div>
                    </Link>
                  ))}
              </div>
            </section>
          </div>
        </main>
        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
}